import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Spinner, Plus, Trash, PencilSimple, Lightning, CalendarBlank,
  ArrowUp, Checks, Warning, Wallet, Receipt, Clock, CheckCircle,
  Package, CurrencyCircleDollar, ArrowsClockwise, Crown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubscriptionPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);

  // Plan dialog
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "", description: "", min_devices: 1, max_devices: 10,
    monthly_price: 0, yearly_price: 0, currency: "TRY",
    trial_days: 0, features: "", sort_order: 0,
  });
  const [planSaving, setPlanSaving] = useState(false);

  // Assign plan dialog
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignPlanId, setAssignPlanId] = useState("");
  const [assignCycle, setAssignCycle] = useState("yearly");
  const [assignLoading, setAssignLoading] = useState(false);
  const [payPage, setPayPage] = useState(1);
  const [payPageSize, setPayPageSize] = useState(20);

  // Change plan dialog
  const [changeDialog, setChangeDialog] = useState(false);
  const [changeTarget, setChangeTarget] = useState(null);
  const [changePlanId, setChangePlanId] = useState("");
  const [changeCalc, setChangeCalc] = useState(null);
  const [changeLoading, setChangeLoading] = useState(false);

  const [renewalInvoices, setRenewalInvoices] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, overviewRes, paymentsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/subscription-plans`),
        axios.get(`${API_URL}/api/admin/subscriptions-overview`),
        axios.get(`${API_URL}/api/admin/subscription-payments`),
        axios.get(`${API_URL}/api/admin/subscription-invoices`),
      ]);
      setPlans(plansRes.data);
      setOverview(overviewRes.data);
      setPayments(paymentsRes.data);
      setRenewalInvoices(invoicesRes.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Plan CRUD ───
  const openPlanDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setPlanForm({
        name: plan.name, description: plan.description || "",
        min_devices: plan.min_devices, max_devices: plan.max_devices,
        monthly_price: plan.monthly_price, yearly_price: plan.yearly_price,
        currency: plan.currency || "TRY", trial_days: plan.trial_days || 0,
        features: (plan.features || []).join("\n"), sort_order: plan.sort_order || 0,
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        name: "", description: "", min_devices: 1, max_devices: 10,
        monthly_price: 0, yearly_price: 0, currency: "TRY",
        trial_days: 0, features: "", sort_order: plans.length + 1,
      });
    }
    setPlanDialog(true);
  };

  const handleSavePlan = async () => {
    if (!planForm.name.trim()) { toast.error("Plan adi gerekli"); return; }
    setPlanSaving(true);
    try {
      const payload = {
        ...planForm,
        features: planForm.features.split("\n").map(f => f.trim()).filter(Boolean),
      };
      if (editingPlan) {
        await axios.put(`${API_URL}/api/admin/subscription-plans/${editingPlan.id}`, payload);
        toast.success("Plan guncellendi");
      } else {
        await axios.post(`${API_URL}/api/admin/subscription-plans`, payload);
        toast.success("Plan olusturuldu");
      }
      setPlanDialog(false);
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setPlanSaving(false); }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Bu plani silmek istediginize emin misiniz?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/subscription-plans/${planId}`);
      toast.success("Plan silindi");
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  // ─── Assign Plan ───
  const openAssignDialog = (customer) => {
    setAssignTarget(customer);
    setAssignPlanId("");
    setAssignCycle("yearly");
    setAssignDialog(true);
  };

  const handleAssignPlan = async () => {
    if (!assignPlanId) { toast.error("Plan secin"); return; }
    setAssignLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${assignTarget.customer_id}/assign-plan`, {
        plan_id: assignPlanId,
        billing_cycle: assignCycle,
      });
      toast.success("Plan atandi");
      setAssignDialog(false);
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setAssignLoading(false); }
  };

  // ─── Change Plan ───
  const openChangeDialog = async (customer) => {
    setChangeTarget(customer);
    setChangePlanId("");
    setChangeCalc(null);
    setChangeDialog(true);
  };

  const handleCalcChange = async (planId) => {
    setChangePlanId(planId);
    if (!planId) { setChangeCalc(null); return; }
    setChangeLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/customers/${changeTarget.customer_id}/calculate-plan-change`,
        { plan_id: planId }
      );
      setChangeCalc(res.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setChangeLoading(false); }
  };

  const handleChangePlan = async () => {
    if (!changePlanId) return;
    setChangeLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/customers/${changeTarget.customer_id}/change-plan`, {
        plan_id: changePlanId,
      });
      toast.success("Plan degistirildi");
      setChangeDialog(false);
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setChangeLoading(false); }
  };

  // ─── Payment ───
  const handlePayment = async (paymentId) => {
    try {
      await axios.put(`${API_URL}/api/admin/subscription-payments/${paymentId}/pay`);
      toast.success("Odeme onaylandi");
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  // ─── Invoice Payment Approval ───
  const handleInvoicePaid = async (invoiceId) => {
    try {
      await axios.post(`${API_URL}/api/admin/subscription-invoices/${invoiceId}/mark-paid`);
      toast.success("Fatura odendi, abonelik yenilendi");
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const getCurrencySymbol = (c) => c === "USD" ? "$" : "\u20ba";

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  const summary = overview?.summary || {};

  return (
    <div className="space-y-6" data-testid="admin-subscription-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Abonelik Yonetimi</h1>
        <p className="text-slate-500 mt-1">Planlar, musteri abonelikleri ve odeme takibi</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Wallet size={20} weight="duotone" className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Toplam Gelir</p>
              <p className="text-lg font-bold text-emerald-700" data-testid="total-revenue">
                {"\u20ba"}{(summary.total_revenue || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Clock size={20} weight="duotone" className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Bekleyen Odeme</p>
              <p className="text-lg font-bold text-amber-700" data-testid="pending-amount">
                {"\u20ba"}{(summary.pending_amount || 0).toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Lightning size={20} weight="duotone" className="text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Aktif Abonelik</p>
              <p className="text-lg font-bold text-slate-900" data-testid="active-subs">
                {summary.active_subscriptions || 0}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
              <Receipt size={20} weight="duotone" className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Toplam Islem</p>
              <p className="text-lg font-bold text-slate-900" data-testid="total-payments">
                {summary.total_payments || 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="customers" className="w-full">
        <TabsList className="bg-slate-100 w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="customers" data-testid="tab-customers">Musteri Abonelikleri</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Faturalar</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Planlar</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">{t("subscription.payment_history")}</TabsTrigger>
        </TabsList>

        {/* ─── Tab: Customer Subscriptions ─── */}
        <TabsContent value="customers" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <CalendarBlank size={18} className="text-emerald-600" /> Musteri Abonelikleri
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>{t("common.customer")}</TableHead>
                      <TableHead>{t("common.plan")}</TableHead>
                      <TableHead>{t("common.period")}</TableHead>
                      <TableHead>{t("subscription.device_count")}</TableHead>
                      <TableHead>{t("common.price")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>{t("common.end_date")}</TableHead>
                      <TableHead>{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(overview?.customers || []).map((c) => {
                      const sub = c.subscription;
                      const plan = c.plan;
                      const isActive = sub?.status === "active";
                      const isTrial = sub?.status === "trial";
                      const isExpired = sub?.status === "expired";
                      const isSuspended = c.status === "suspended";
                      const sym = getCurrencySymbol(sub?.currency);

                      return (
                        <TableRow key={c.customer_id} data-testid={`sub-row-${c.customer_id}`}
                          className={isSuspended ? "bg-red-50/50" : ""}>
                          <TableCell>
                            <div>
                              <span className="font-medium">{c.customer_name}</span>
                              {c.company_name && (
                                <span className="text-xs text-slate-400 block">{c.company_name}</span>
                              )}
                              {isSuspended && <Badge className="bg-red-100 text-red-700 text-[10px] mt-1">Askida</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            {plan ? (
                              <Badge className={`${plan.deleted ? "bg-slate-100 text-slate-500" : "bg-indigo-100 text-indigo-700"}`}>
                                {plan.name}{plan.deleted ? " (silindi)" : ""}
                              </Badge>
                            ) : sub ? (
                              <Badge className="bg-slate-100 text-slate-500">
                                {sub.plan_name || "Eski Sistem"}
                              </Badge>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {sub?.billing_cycle === "monthly" ? "Aylik" : sub?.billing_cycle === "yearly" ? "Yillik" : "-"}
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-sm">{c.device_count}</span>
                            {plan && <span className="text-xs text-slate-400">/{plan.max_devices}</span>}
                          </TableCell>
                          <TableCell className="font-bold text-sm">
                            {sub ? `${sym}${sub.amount?.toFixed(0) || 0}` : "-"}
                          </TableCell>
                          <TableCell>
                            {isSuspended && <Badge className="bg-red-100 text-red-700">Askida</Badge>}
                            {isTrial && <Badge className="bg-blue-100 text-blue-700">Deneme</Badge>}
                            {isActive && !isSuspended && <Badge className="bg-emerald-100 text-emerald-700">{t("common.active")}</Badge>}
                            {isExpired && !isSuspended && <Badge className="bg-red-100 text-red-700">Suresi Dolmus</Badge>}
                            {!sub && !isSuspended && <Badge className="bg-slate-100 text-slate-500">Plansiz</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {sub?.end_date ? new Date(sub.end_date).toLocaleDateString("tr-TR") : "-"}
                            {isTrial && sub?.trial_end_date && (
                              <span className="block text-blue-500">
                                Deneme: {new Date(sub.trial_end_date).toLocaleDateString("tr-TR")}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {!sub && !isSuspended && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200"
                                  onClick={() => openAssignDialog(c)} data-testid={`assign-plan-${c.customer_id}`}>
                                  <Lightning size={12} className="mr-1" /> Plan Ata
                                </Button>
                              )}
                              {(isActive || isTrial) && !isSuspended && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-200"
                                  onClick={() => openChangeDialog(c)} data-testid={`change-plan-${c.customer_id}`}>
                                  <ArrowsClockwise size={12} className="mr-1" /> Degistir
                                </Button>
                              )}
                              {(isExpired || isSuspended) && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200"
                                  onClick={() => openAssignDialog(c)} data-testid={`renew-plan-${c.customer_id}`}>
                                  <Checks size={12} className="mr-1" /> Yenile
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!overview?.customers || overview.customers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                          Henuz musteri yok
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: Invoices (Yenileme Faturalari) ─── */}
        <TabsContent value="invoices" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Receipt size={18} className="text-amber-600" /> Yenileme Faturalari
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renewalInvoices.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt size={36} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Henuz yenileme faturasi yok</p>
                  <p className="text-xs text-slate-400 mt-1">Faturalar abonelik bitis tarihine yaklastiginda otomatik olusturulur</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Musteri</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead className="hidden sm:table-cell">Donem</TableHead>
                      <TableHead>Son Odeme</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renewalInvoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-medium">{inv.customer_name || inv.customer_id?.slice(0,8)}</TableCell>
                        <TableCell><Badge className="bg-indigo-100 text-indigo-700 text-xs">{inv.plan_name}</Badge></TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-slate-500">{inv.billing_cycle === "monthly" ? "Aylik" : "Yillik"}</TableCell>
                        <TableCell className="text-sm">{new Date(inv.due_date).toLocaleDateString("tr-TR")}</TableCell>
                        <TableCell className="text-right font-bold">{inv.currency === "USD" ? "$" : "\u20ba"}{(inv.amount || 0).toLocaleString("tr-TR")}</TableCell>
                        <TableCell>
                          <Badge className={
                            inv.status === "paid" ? "bg-emerald-100 text-emerald-700" :
                            inv.status === "overdue" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }>
                            {inv.status === "paid" ? "Odendi" : inv.status === "overdue" ? "Gecikti" : "Bekliyor"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {inv.status !== "paid" && (
                            <Button size="sm" variant="outline"
                              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              onClick={() => handleInvoicePaid(inv.id)}
                              data-testid={`approve-invoice-${inv.id}`}>
                              <CheckCircle size={14} className="mr-1" /> Odendi
                            </Button>
                          )}
                          {inv.status === "paid" && (
                            <span className="text-xs text-slate-400">
                              {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("tr-TR") : ""}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: Plans ─── */}
        <TabsContent value="plans" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Package size={18} className="text-indigo-600" /> Abonelik Planlari
              </CardTitle>
              <Button size="sm" onClick={() => openPlanDialog()} data-testid="add-plan-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Plus size={14} className="mr-1" /> Yeni Plan
              </Button>
            </CardHeader>
            <CardContent>
              {plans.length === 0 ? (
                <div className="text-center py-12">
                  <Package size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Henuz plan olusturulmamis</p>
                  <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={() => openPlanDialog()}>
                    <Plus size={14} className="mr-1" /> Ilk Plani Olustur
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {plans.map((plan) => (
                    <Card key={plan.id} data-testid={`plan-card-${plan.id}`}
                      className={`border-2 transition-all ${plan.is_active ? "border-indigo-200 hover:border-indigo-300" : "border-slate-200 opacity-60"}`}>
                      <CardContent className="p-5 space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                              {plan.sort_order <= 1 && <Lightning size={16} className="text-amber-500" />}
                              {plan.sort_order === 2 && <Crown size={16} className="text-indigo-500" />}
                              {plan.sort_order >= 3 && <Crown size={16} className="text-purple-500" />}
                              {plan.name}
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">{plan.description}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => openPlanDialog(plan)} data-testid={`edit-plan-${plan.id}`}>
                              <PencilSimple size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                              onClick={() => handleDeletePlan(plan.id)} data-testid={`delete-plan-${plan.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-slate-900">
                            {getCurrencySymbol(plan.currency)}{plan.monthly_price.toLocaleString("tr-TR")}
                          </span>
                          <span className="text-slate-500 text-sm">/ay</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Yillik: {getCurrencySymbol(plan.currency)}{plan.yearly_price.toLocaleString("tr-TR")}/yil
                          {plan.yearly_price < plan.monthly_price * 12 && (
                            <Badge className="bg-emerald-100 text-emerald-700 ml-2 text-[10px]">
                              %{Math.round((1 - plan.yearly_price / (plan.monthly_price * 12)) * 100)} indirim
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="bg-slate-100 px-2 py-1 rounded text-slate-700">
                            {plan.min_devices}-{plan.max_devices} cihaz
                          </span>
                          {plan.trial_days > 0 && (
                            <span className="bg-blue-50 px-2 py-1 rounded text-blue-700">
                              {plan.trial_days} gun deneme
                            </span>
                          )}
                        </div>
                        {plan.features && plan.features.length > 0 && (
                          <ul className="space-y-1.5">
                            {plan.features.map((f, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        )}
                        {!plan.is_active && (
                          <Badge className="bg-slate-100 text-slate-500">{t("common.inactive")}</Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: Payments ─── */}
        <TabsContent value="payments" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Receipt size={18} className="text-purple-600" /> Odeme Gecmisi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Henuz odeme kaydi yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>{t("common.date")}</TableHead>
                        <TableHead>{t("common.customer")}</TableHead>
                        <TableHead>{t("common.plan")}</TableHead>
                        <TableHead>{t("common.type")}</TableHead>
                        <TableHead>{t("common.description")}</TableHead>
                        <TableHead>{t("common.amount")}</TableHead>
                        <TableHead>{t("common.status")}</TableHead>
                        <TableHead>{t("common.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.slice((payPage - 1) * payPageSize, payPage * payPageSize).map((p) => (
                        <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(p.created_at).toLocaleDateString("tr-TR")}
                          </TableCell>
                          <TableCell className="font-medium">{p.customer_name}</TableCell>
                          <TableCell>
                            {p.plan_name ? (
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs">{p.plan_name}</Badge>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              p.type === "activation" ? "bg-emerald-100 text-emerald-700" :
                              p.type === "renewal" ? "bg-indigo-100 text-indigo-700" :
                              "bg-amber-100 text-amber-700"
                            }>
                              {p.type === "activation" ? "Aktivasyon" : p.type === "renewal" ? "Yenileme" : "Yukseltme"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                            {p.description}
                          </TableCell>
                          <TableCell className="font-bold">
                            {getCurrencySymbol(p.currency)}{(p.amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {p.status === "paid" ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle size={12} className="mr-1" /> Odendi
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock size={12} className="mr-1" /> Bekliyor
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.status === "pending" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200"
                                onClick={() => handlePayment(p.id)} data-testid={`pay-btn-${p.id}`}>
                                <Checks size={12} className="mr-1" /> Onayla
                              </Button>
                            )}
                            {p.status === "paid" && p.paid_at && (
                              <span className="text-xs text-slate-400">
                                {new Date(p.paid_at).toLocaleDateString("tr-TR")}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {payments.length > payPageSize && (
                <Pagination total={payments.length} page={payPage} pageSize={payPageSize}
                  onPageChange={setPayPage} onPageSizeChange={setPayPageSize} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ─── Plan Create/Edit Dialog ─── */}
      <Dialog open={planDialog} onOpenChange={setPlanDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? "Plan Duzenle" : "Yeni Plan Olustur"}</DialogTitle>
            <DialogDescription>
              Abonelik plani detaylarini girin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Plan Adi *</Label>
                <Input value={planForm.name} onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  placeholder="ornek: Starter" data-testid="plan-name-input" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">{t("common.description")}</Label>
                <Input value={planForm.description} onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                  placeholder="Plan aciklamasi" data-testid="plan-desc-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Min Cihaz</Label>
                <Input type="number" min="1" value={planForm.min_devices}
                  onChange={(e) => setPlanForm({...planForm, min_devices: parseInt(e.target.value) || 1})}
                  data-testid="plan-min-devices" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max Cihaz</Label>
                <Input type="number" min="1" value={planForm.max_devices}
                  onChange={(e) => setPlanForm({...planForm, max_devices: parseInt(e.target.value) || 10})}
                  data-testid="plan-max-devices" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Aylik Fiyat</Label>
                <Input type="number" min="0" step="10" value={planForm.monthly_price}
                  onChange={(e) => setPlanForm({...planForm, monthly_price: parseFloat(e.target.value) || 0})}
                  data-testid="plan-monthly-price" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Yillik Fiyat</Label>
                <Input type="number" min="0" step="100" value={planForm.yearly_price}
                  onChange={(e) => setPlanForm({...planForm, yearly_price: parseFloat(e.target.value) || 0})}
                  data-testid="plan-yearly-price" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Para Birimi</Label>
                <Select value={planForm.currency} onValueChange={(v) => setPlanForm({...planForm, currency: v})}>
                  <SelectTrigger data-testid="plan-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">TRY (Turk Lirasi)</SelectItem>
                    <SelectItem value="USD">USD (Dolar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deneme Suresi (gun)</Label>
                <Input type="number" min="0" value={planForm.trial_days}
                  onChange={(e) => setPlanForm({...planForm, trial_days: parseInt(e.target.value) || 0})}
                  data-testid="plan-trial-days" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Siralama</Label>
                <Input type="number" min="0" value={planForm.sort_order}
                  onChange={(e) => setPlanForm({...planForm, sort_order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ozellikler (her satir bir ozellik)</Label>
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[100px] resize-y"
                value={planForm.features}
                onChange={(e) => setPlanForm({...planForm, features: e.target.value})}
                placeholder={"10 cihaza kadar\nEmail destek\nTemel raporlama"}
                data-testid="plan-features-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSavePlan} disabled={planSaving}
              data-testid="save-plan-btn">
              {planSaving ? "Kaydediliyor..." : editingPlan ? t("common.update") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Assign Plan Dialog ─── */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("subscription.assign_plan")}</DialogTitle>
            <DialogDescription>
              {assignTarget?.customer_name} icin abonelik plani secin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Plan</Label>
              <Select value={assignPlanId} onValueChange={setAssignPlanId}>
                <SelectTrigger data-testid="assign-plan-select"><SelectValue placeholder="Plan secin" /></SelectTrigger>
                <SelectContent>
                  {plans.filter(p => p.is_active).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.min_devices}-{p.max_devices} cihaz) - {getCurrencySymbol(p.currency)}{p.monthly_price}/ay
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Odeme Periyodu</Label>
              <Select value={assignCycle} onValueChange={setAssignCycle}>
                <SelectTrigger data-testid="assign-cycle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{t("common.monthly")}</SelectItem>
                  <SelectItem value="yearly">{t("common.yearly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {assignPlanId && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                {(() => {
                  const sel = plans.find(p => p.id === assignPlanId);
                  if (!sel) return null;
                  const sym = getCurrencySymbol(sel.currency);
                  const price = assignCycle === "monthly" ? sel.monthly_price : sel.yearly_price;
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Secilen Plan</span>
                        <span className="font-bold">{sel.name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Cihaz Limiti</span>
                        <span>{sel.min_devices}-{sel.max_devices}</span>
                      </div>
                      {sel.trial_days > 0 && (
                        <div className="flex justify-between text-sm text-blue-600">
                          <span>{t("subscription.trial_period")}</span>
                          <span>{sel.trial_days} gun (ucretsiz)</span>
                        </div>
                      )}
                      <hr className="border-slate-200" />
                      <div className="flex justify-between text-base font-bold">
                        <span>{assignCycle === "monthly" ? "Aylik" : "Yillik"} Ucret</span>
                        <span className="text-emerald-600">{sym}{price.toLocaleString("tr-TR")}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleAssignPlan}
              disabled={!assignPlanId || assignLoading} data-testid="confirm-assign-btn">
              {assignLoading ? "Isleniyor..." : "Plan Ata"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Change Plan Dialog ─── */}
      <Dialog open={changeDialog} onOpenChange={setChangeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("subscription.change_plan")}</DialogTitle>
            <DialogDescription>
              {changeTarget?.customer_name} icin yeni plan secin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Yeni Plan</Label>
              <Select value={changePlanId} onValueChange={handleCalcChange}>
                <SelectTrigger data-testid="change-plan-select"><SelectValue placeholder="Yeni plan secin" /></SelectTrigger>
                <SelectContent>
                  {plans.filter(p => p.is_active && p.id !== changeTarget?.subscription?.plan_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.min_devices}-{p.max_devices} cihaz)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {changeLoading && (
              <div className="flex justify-center py-4">
                <Spinner size={24} className="animate-spin text-indigo-600" />
              </div>
            )}
            {changeCalc && !changeLoading && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                {!changeCalc.fits && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded flex items-start gap-2 mb-2">
                    <Warning size={14} className="text-red-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-700">
                      Musteri {changeCalc.device_count} cihaza sahip, bu plan max {changeCalc.max_devices} cihaz destekliyor!
                    </p>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Mevcut Plan</span>
                  <span>{changeCalc.old_plan_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Yeni Plan</span>
                  <span className="font-bold">{changeCalc.new_plan_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Kalan Gun</span>
                  <span>{changeCalc.remaining_days} gun</span>
                </div>
                <hr className="border-slate-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Eski Kredi</span>
                  <span className="text-emerald-600">
                    {getCurrencySymbol(changeCalc.currency)}{changeCalc.pro_rata_credit?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Yeni Maliyet</span>
                  <span>{getCurrencySymbol(changeCalc.currency)}{changeCalc.new_pro_rata_cost?.toFixed(2)}</span>
                </div>
                <hr className="border-slate-200" />
                <div className="flex justify-between text-base font-bold">
                  <span>{t("subscription.pay_difference")}</span>
                  <span className={changeCalc.difference > 0 ? "text-amber-600" : "text-emerald-600"}>
                    {getCurrencySymbol(changeCalc.currency)}{changeCalc.difference?.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleChangePlan}
              disabled={!changePlanId || changeLoading || (changeCalc && !changeCalc.fits)}
              data-testid="confirm-change-btn">
              {changeLoading ? "Isleniyor..." : t("subscription.change_plan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptionPage;
